import {
  type PopupTarget,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { GroupingIcon, SettingsIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { autoPlacement, offset, shift } from '@floating-ui/dom';
import { html, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';

import {
  popGroupSetting,
  popSelectGroupByProperty,
} from '../../../../core/group-by/setting.js';
import { groupTraitKey } from '../../../../core/group-by/trait.js';
import { type DataViewI18nKey, dataViewI18nT } from '../../../../core/i18n.js';
import {
  renderTemporalModeGroup,
  renderTemporalRangeControls,
} from '../../../shared/toolbar/temporal-toolbar.js';
import type { GanttZoomLevel } from '../../define.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import { openBarDisplaySettings } from '../bars/bar-display-settings.js';
import {
  todayButtonStyle,
  zoomActionGroupStyle,
  zoomButtonStyle,
  zoomGroupStyle,
  zoomIconButtonStyle,
  zoomNavGroupStyle,
  zoomRangeTitleStyle,
  zoomToolbarStyle,
} from './time-axis-css.js';

const ZOOMS: ReadonlyArray<{
  key: GanttZoomLevel;
  label: DataViewI18nKey;
}> = [
  { key: 'day', label: 'gantt.zoom.day' },
  { key: 'week', label: 'gantt.zoom.week' },
  { key: 'month', label: 'gantt.zoom.month' },
  { key: 'quarter', label: 'gantt.zoom.quarter' },
];

/**
 * 4-button zoom selector + "Today" button. Persists the zoom level
 * through `view.setZoomLevel` (Y.js write → CRDT sync). "Today" is a
 * transient scroll action; it does not write to the doc. Both handlers
 * are wired by the parent via the `onScrollToToday` callback.
 */
export class GanttZoomToolbar extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor view!: GanttSingleView;

  @property({ attribute: false })
  accessor onScrollToToday: (() => void) | undefined;

  @property({ attribute: false })
  accessor onScrollToPreviousRange: (() => void) | undefined;

  @property({ attribute: false })
  accessor onScrollToNextRange: (() => void) | undefined;

  @property({ attribute: false })
  accessor rangeTitle = '';

  override connectedCallback(): void {
    super.connectedCallback();
    this.classList.add(zoomToolbarStyle);
  }

  private readonly groupMenuMiddleware = [
    autoPlacement({ allowedPlacements: ['bottom-end', 'top-end'] }),
    offset(4),
    shift({ crossAxis: true }),
  ];

  private openGroupMenu(target: PopupTarget): void {
    const groupTrait = this.view.traitGet(groupTraitKey);
    if (!groupTrait) return;

    const reopen = () => this.openGroupMenu(target);
    if (!groupTrait.property$.value) {
      popSelectGroupByProperty(
        target,
        groupTrait,
        {
          onSelect: () =>
            popGroupSetting(
              target,
              groupTrait,
              reopen,
              undefined,
              this.groupMenuMiddleware
            ),
        },
        this.groupMenuMiddleware
      );
      return;
    }

    popGroupSetting(
      target,
      groupTrait,
      reopen,
      undefined,
      this.groupMenuMiddleware
    );
  }

  override render(): TemplateResult {
    const current = this.view.zoomLevel$.value;
    const groupTrait = this.view.traitGet(groupTraitKey);
    const zoomOptions = ZOOMS.map(({ key, label }) => {
      const displayLabel = dataViewI18nT(label);
      return {
        ariaLabel: dataViewI18nT('gantt.accessibility.switchZoom', {
          zoom: displayLabel,
        }),
        key,
        label: displayLabel,
      };
    });
    return html`
      ${renderTemporalModeGroup<GanttZoomLevel>({
        activeMode: current,
        buttonClass: zoomButtonStyle,
        groupClass: zoomGroupStyle,
        onModeChange: zoom => this.view.setZoomLevel(zoom),
        options: zoomOptions,
      })}
      ${renderTemporalRangeControls({
        buttonClass: zoomButtonStyle,
        groupClass: zoomNavGroupStyle,
        iconButtonClass: zoomIconButtonStyle,
        nextLabel: dataViewI18nT('gantt.accessibility.nextRange'),
        onNext: () => this.onScrollToNextRange?.(),
        onPrevious: () => this.onScrollToPreviousRange?.(),
        onToday: () => this.onScrollToToday?.(),
        previousLabel: dataViewI18nT('gantt.accessibility.previousRange'),
        rangeTitle: this.rangeTitle,
        rangeTitleClass: zoomRangeTitleStyle,
        todayAriaLabel: dataViewI18nT('gantt.accessibility.goToday'),
        todayButtonExtraClass: todayButtonStyle,
        todayLabel: dataViewI18nT('gantt.action.today'),
      })}
      <div class=${zoomActionGroupStyle}>
        ${groupTrait
          ? html`<button
              class=${zoomIconButtonStyle}
              title=${dataViewI18nT('gantt.settings.group')}
              aria-label=${dataViewI18nT('gantt.settings.group')}
              @click=${(e: MouseEvent) =>
                this.openGroupMenu(
                  popupTargetFromElement(e.currentTarget as HTMLElement)
                )}
            >
              ${GroupingIcon()}
            </button>`
          : null}
        <button
          class=${zoomIconButtonStyle}
          title=${dataViewI18nT('gantt.settings.barDisplay')}
          aria-label=${dataViewI18nT('gantt.settings.barDisplay')}
          @click=${(e: MouseEvent) =>
            openBarDisplaySettings(
              popupTargetFromElement(e.currentTarget as HTMLElement),
              this.view
            )}
        >
          ${SettingsIcon()}
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-zoom-toolbar': GanttZoomToolbar;
  }
}
